import React, { useEffect, useRef, useState } from "react";
import { Button, FormGroup, Input, Label } from "reactstrap";
import notifications from "../../common/Components/Notifications/Notification";
import { isGoogleAnalyticsMeasurementId } from "../../utils/cataloguePublicAnalytics";

const studioOrganizationId = (): string => {
  try {
    const auth = JSON.parse(localStorage.getItem("authUser") || "null");
    return String(auth?.users?.organizationResponse?._id || "").trim();
  } catch {
    return "";
  }
};

const authHeaders = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
});

type StudioAnalyticsSettingsProps = {
  className?: string;
  open: boolean;
  onOpen: () => void;
  onClose: () => void;
};

const StudioAnalyticsSettings: React.FC<StudioAnalyticsSettingsProps> = ({
  className,
  open,
  onOpen,
  onClose,
}) => {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [organizationId, setOrganizationId] = useState("");
  const [gaMeasurementId, setGaMeasurementId] = useState("");
  const [gaSavedId, setGaSavedId] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!open) {
      return;
    }
    const onPointer = (event: MouseEvent) => {
      if (!wrapRef.current || wrapRef.current.offsetParent === null) {
        return;
      }
      if (!wrapRef.current.contains(event.target as Node)) {
        onClose();
      }
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  useEffect(() => {
    if (!open) {
      return;
    }
    const orgId = studioOrganizationId();
    setOrganizationId(orgId);
    if (!orgId) {
      setGaMeasurementId("");
      setGaSavedId("");
      return;
    }
    let cancelled = false;
    const load = async () => {
      if (wrapRef.current?.offsetParent === null) {
        return;
      }
      try {
        setLoading(true);
        const response = await fetch(
          `${process.env.REACT_APP_SERVERURL}/organization/organizations?offset=0&limit=1&id=${encodeURIComponent(orgId)}`,
          { headers: authHeaders() }
        );
        const result = await response.json();
        if (cancelled) {
          return;
        }
        const row = Array.isArray(result?.data?.docs) ? result.data.docs[0] : null;
        const gaId = String(row?.googleAnalyticsMeasurementId || "").trim();
        setGaMeasurementId(gaId);
        setGaSavedId(gaId);
        if (row?._id) {
          setOrganizationId(String(row._id));
        }
      } catch {
        if (!cancelled) {
          notifications({
            title: "Google Analytics",
            message: "Could not load the studio Analytics ID.",
            type: "warning",
          });
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [open]);

  const handleSave = async () => {
    const orgId = organizationId || studioOrganizationId();
    if (!orgId) {
      notifications({
        title: "Google Analytics",
        message: "Could not resolve this studio's organization.",
        type: "warning",
      });
      return;
    }
    const next = gaMeasurementId.trim().toUpperCase();
    if (next && !isGoogleAnalyticsMeasurementId(next)) {
      notifications({
        title: "Google Analytics",
        message: "Use a GA4 ID like G-XXXXXXXX, or leave the field blank.",
        type: "warning",
      });
      return;
    }
    try {
      setIsSaving(true);
      const response = await fetch(`${process.env.REACT_APP_SERVERURL}/organization/update`, {
        method: "PUT",
        headers: authHeaders(),
        body: JSON.stringify({
          id: orgId,
          googleAnalyticsMeasurementId: next,
        }),
      });
      const result = await response.json();
      if (!response.ok || !result?.success) {
        throw new Error(result?.message || "Could not save Analytics ID");
      }
      setGaMeasurementId(next);
      setGaSavedId(next);
      notifications({
        title: "Google Analytics",
        message: next
          ? "Saved for this studio. All catalogues and share links will use this ID."
          : "Studio Analytics ID cleared.",
        type: "success",
      });
      onClose();
    } catch (error: any) {
      notifications({
        title: "Google Analytics",
        message: error?.message || "Could not save Analytics ID",
        type: "danger",
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className={`catalogue-studio-settings ${className || ""}`} ref={wrapRef}>
      <button
        type="button"
        className="catalogue-studio-settings-btn"
        aria-label="Studio Google Analytics"
        aria-expanded={open}
        onClick={() => (open ? onClose() : onOpen())}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden>
          <path
            fill="currentColor"
            d="M19.14 12.94c.04-.31.06-.63.06-.94 0-.31-.02-.63-.06-.94l2.03-1.58a.5.5 0 0 0 .12-.64l-1.92-3.32a.5.5 0 0 0-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54A.5.5 0 0 0 14.4 2h-3.84a.5.5 0 0 0-.48.41l-.36 2.54c-.59.24-1.13.56-1.62.94l-2.39-.96a.5.5 0 0 0-.59.22L2.74 8.87a.5.5 0 0 0 .12.64l2.03 1.58c-.04.31-.07.63-.07.94s.03.63.07.94l-2.03 1.58c-.18.14-.23.41-.12.64l1.92 3.32c.12.22.37.3.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32a.5.5 0 0 0-.12-.64l-2.03-1.58ZM12 15.6A3.6 3.6 0 1 1 12 8.4a3.6 3.6 0 0 1 0 7.2Z"
          />
        </svg>
      </button>
      {open ? (
        <div className="catalogue-studio-settings-panel" role="dialog" aria-label="Studio Google Analytics">
          <h6 className="catalogue-panel-title mb-2">Google Analytics (this studio)</h6>
          {loading ? <p className="text-muted small mb-2">Loading…</p> : null}
          <p className="text-muted small mb-2">
            Paste one GA4 measurement ID. It applies to every catalogue and every share link for this
            studio — not per link. We never send phone, email, or name to Google.
          </p>
          <p className="small fw-semibold mb-1">Find the measurement ID in Google Analytics</p>
          <ol className="catalogue-studio-settings-steps">
            <li>
              Open{" "}
              <a href="https://analytics.google.com" target="_blank" rel="noopener noreferrer">
                analytics.google.com
              </a>
            </li>
            <li>Admin (gear, bottom left)</li>
            <li>Data collection → Data streams → your website</li>
            <li>Copy Measurement ID (starts with G-)</li>
          </ol>
          <FormGroup className="mb-2">
            <Label for="studioGaId">Measurement ID</Label>
            <Input
              id="studioGaId"
              value={gaMeasurementId}
              onChange={(e) => setGaMeasurementId(e.target.value)}
              placeholder="G-XXXXXXXX"
              autoComplete="off"
              disabled={loading || saving}
            />
          </FormGroup>
          {gaSavedId ? (
            <small className="text-muted d-block mb-2">Currently saved: {gaSavedId}</small>
          ) : null}
          <div className="d-flex justify-content-end gap-2">
            <Button color="secondary" outline size="sm" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button
              size="sm"
              style={{ backgroundColor: "#0071E3", borderColor: "#0071E3" }}
              onClick={handleSave}
              disabled={loading || saving}
            >
              {saving ? "Saving…" : "Save"}
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default StudioAnalyticsSettings;
